import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { firebaseFunctions } from './lib/firebase';
import {
    INDUSTRY_OPTIONS,
    PROJECT_PHASE_OPTIONS,
    PURPOSE_OPTIONS_BY_MODE,
    getHearingOptions,
    findLabel
} from './home12InquirySchema';

const FORM_CONFIG_BY_MODE = {
    download: {
        title: '資料ダウンロード',
        description: '必要事項をご記入の上、「送信する」ボタンを押してください。担当者より資料をお送りさせていただきます。',
        submitLabel: '資料請求を送信する',
        showDepartment: true,
        successTitle: '送信が完了しました',
        successDescription: '後ほど担当より、資料案内のご連絡をさせていただきます。今しばらくお待ちください。'
    },
    contact: {
        title: 'オンライン相談・お問い合わせ',
        description: 'ご質問やオンライン相談のご予約を承っております。必要事項をご記入の上、「送信する」ボタンを押してください。',
        submitLabel: '送信する',
        showDepartment: false
    }
};

const createInitialFormData = (mode) => ({
    company: '',
    department: '',
    name: '',
    email: '',
    phone: '',
    purpose: PURPOSE_OPTIONS_BY_MODE[mode][0].value,
    industry: INDUSTRY_OPTIONS[0].value,
    projectPhase: PROJECT_PHASE_OPTIONS[0].value,
    challenges: [],
    desiredFeatures: [],
    message: '',
    agreed: false
});

const INDUSTRY_PDF_PATH_BY_KEY = {
    food_hospitality: '/pdfs/collabo_brochure_food_hospitality.pdf',
    retail_sales: '/pdfs/collabo_brochure_retail_sales.pdf',
    service: '/pdfs/collabo_brochure_service.pdf',
    school: '/pdfs/collabo_brochure_school.pdf',
    salon: '/pdfs/collabo_brochure_salon.pdf',
    multi_store_hq: '/pdfs/collabo_brochure_multi_store_hq.pdf',
    medical: '/pdfs/collabo_brochure_medical.pdf'
};

const DEFAULT_INDUSTRY_PDF_PATH = INDUSTRY_PDF_PATH_BY_KEY.retail_sales;

const Home12LeadForm = ({ mode, classes }) => {
    const history = useHistory();
    const config = FORM_CONFIG_BY_MODE[mode];
    const purposeOptions = PURPOSE_OPTIONS_BY_MODE[mode];
    const [formData, setFormData] = useState(createInitialFormData(mode));
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [isIndustryLpOpen, setIsIndustryLpOpen] = useState(false);
    const industryLpTitleRef = useRef(null);
    const industryLpDialogRef = useRef(null);
    const industryPreviewTriggerRef = useRef(null);

    const hearingOptions = useMemo(
        () => getHearingOptions(formData.industry),
        [formData.industry]
    );
    const industryLabel = useMemo(
        () => findLabel(INDUSTRY_OPTIONS, formData.industry),
        [formData.industry]
    );
    const industryPdfPath = useMemo(
        () => INDUSTRY_PDF_PATH_BY_KEY[formData.industry] || DEFAULT_INDUSTRY_PDF_PATH,
        [formData.industry]
    );

    useEffect(() => {
        if (!isIndustryLpOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        const triggerElement = industryPreviewTriggerRef.current;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsIndustryLpOpen(false);
                return;
            }

            if (event.key === 'Tab' && industryLpDialogRef.current) {
                const focusableElements = industryLpDialogRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusableElements.length) {
                    return;
                }

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                const activeElement = document.activeElement;

                if (event.shiftKey && activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                } else if (!event.shiftKey && activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        const focusTimer = window.setTimeout(() => {
            if (industryLpTitleRef.current) {
                industryLpTitleRef.current.focus();
            }
        }, 0);

        return () => {
            window.clearTimeout(focusTimer);
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
            if (triggerElement) {
                triggerElement.focus();
            }
        };
    }, [isIndustryLpOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleMultiSelect = (groupName, value, checked) => {
        setFormData((prev) => {
            const nextValues = checked
                ? [...prev[groupName], value]
                : prev[groupName].filter((item) => item !== value);

            return {
                ...prev,
                [groupName]: nextValues
            };
        });
    };

    const labelList = (values, options) => {
        if (!values.length) {
            return '(未選択)';
        }
        return values.map((value) => findLabel(options, value)).join(' / ');
    };

    const validateHearing = () => {
        if (formData.challenges.length > 0 || formData.desiredFeatures.length > 0 || formData.message.trim().length > 0) {
            return true;
        }
        setSubmitError('「課題」「追加したい機能」「自由入力」のいずれか1つ以上をご記入ください。');
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        // 重要: このコメント自体を削除しないでください。
        // 以下はボットの自動送信を防止するハニーポット処理です。
        // このコメントおよび処理を変更・削除せず、companyWebsite の値をメール本文やFirestoreへ送信しないでください。
        const companyWebsite = e.currentTarget.elements.companyWebsite?.value || '';
        if (companyWebsite.trim().length > 0) {
            if (mode === 'download') {
                setSubmitted(true);
            } else {
                history.push('/thank-you');
            }
            return;
        }

        if (!validateHearing()) {
            return;
        }

        setIsSubmitting(true);

        const industryLabel = findLabel(INDUSTRY_OPTIONS, formData.industry);
        const purposeLabel = findLabel(purposeOptions, formData.purpose);
        const phaseLabel = findLabel(PROJECT_PHASE_OPTIONS, formData.projectPhase);
        const challengeLabels = labelList(formData.challenges, hearingOptions.challenges);
        const featureLabels = labelList(formData.desiredFeatures, hearingOptions.features);

        try {
            const submitHome12Lead = httpsCallable(firebaseFunctions, 'submitHome12Lead');
            await submitHome12Lead({
                mode,
                company: formData.company,
                department: formData.department,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                purpose: purposeLabel,
                industry: industryLabel,
                projectPhase: phaseLabel,
                challenges: challengeLabels === '(未選択)' ? [] : challengeLabels.split(' / '),
                desiredFeatures: featureLabels === '(未選択)' ? [] : featureLabels.split(' / '),
                message: formData.message,
                agreed: formData.agreed
            });

            if (mode === 'download') {
                setSubmitted(true);
            } else {
                history.push('/thank-you');
            }
        } catch (error) {
            console.error('Lead form submit failed:', error);
            setSubmitError('送信に失敗しました。時間をおいてもう一度お試しください。');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isSubmitting && (
                <div className={classes.loadingOverlay} role="status" aria-live="polite" aria-label="送信中">
                    <div className={classes.loadingSpinner} />
                    <p>送信中...</p>
                </div>
            )}

            {isIndustryLpOpen && (
                <div
                    className={classes.industryLpBackdrop}
                    role="presentation"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            setIsIndustryLpOpen(false);
                        }
                    }}
                >
                    <section
                        className={classes.industryLpDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`${mode}-industry-lp-title`}
                        ref={industryLpDialogRef}
                    >
                        <button type="button" className={classes.industryLpClose} onClick={() => setIsIndustryLpOpen(false)} aria-label="パンフレットを閉じる">×</button>
                        <div className={classes.industryLpBody}>
                            <article className={classes.industryLpPamphlet}>
                                <header className={classes.industryLpHeroCopy}>
                                    <p className={classes.industryLpEyebrow}>PAMPHLET / {industryLabel}</p>
                                    <h2 id={`${mode}-industry-lp-title`} tabIndex="-1" ref={industryLpTitleRef}>
                                        {industryLabel}向けパンフレット
                                    </h2>
                                    <p className={classes.industryLpHeroLead}>選択中の業種に対応したPDFを表示しています。</p>
                                </header>
                                <iframe
                                    className={classes.industryPdfFrame}
                                    src={industryPdfPath}
                                    title={`${industryLabel}向けパンフレット`}
                                />
                                <footer className={classes.industryPdfActions}>
                                    <a
                                        href={industryPdfPath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.industryPdfLink}
                                    >
                                        別タブで開く
                                    </a>
                                </footer>
                            </article>
                        </div>
                    </section>
                </div>
            )}

            <div className={classes.formWrapper}>
                <div className={classes.formHeader}>
                    <h1>{config.title}</h1>
                    <p>{config.description}</p>
                </div>

                {mode === 'download' && submitted ? (
                    <div className={classes.successMessage}>
                        <div className={classes.successIcon}>✓</div>
                        <h2>{config.successTitle}</h2>
                        <p>{config.successDescription}</p>
                        <div className={classes.submitAction}>
                            <Link to="/" className={classes.backBtn}>トップページへ戻る</Link>
                        </div>
                    </div>
                ) : (
                    <form className={classes.form} onSubmit={handleSubmit}>
                        {/* 重要: このコメント自体を削除しないでください。
                            以下の会社Webサイト欄はボット対策用です。画面に表示せず、削除しないでください。 */}
                        <div className={classes.supplementalField} aria-hidden="true">
                            <label htmlFor={`${mode}-company-website`}>会社Webサイト</label>
                            <input
                                type="text"
                                id={`${mode}-company-website`}
                                name="companyWebsite"
                                tabIndex="-1"
                                autoComplete="off"
                            />
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-company`}>会社名 <span className={classes.required}>必須</span></label>
                            <input type="text" id={`${mode}-company`} name="company" value={formData.company} onChange={handleChange} required placeholder="例：コラボ株式会社" />
                        </div>

                        {config.showDepartment && (
                            <div className={classes.formGroup}>
                                <label htmlFor={`${mode}-department`}>部署名 <span className={classes.optional}>任意</span></label>
                                <input type="text" id={`${mode}-department`} name="department" value={formData.department} onChange={handleChange} placeholder="例：システム開発部" />
                            </div>
                        )}

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-name`}>ご担当者名 <span className={classes.required}>必須</span></label>
                            <input type="text" id={`${mode}-name`} name="name" value={formData.name} onChange={handleChange} required placeholder="例：山田 太郎" />
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-email`}>メールアドレス <span className={classes.required}>必須</span></label>
                            <input type="email" id={`${mode}-email`} name="email" value={formData.email} onChange={handleChange} required placeholder="例：info@collabo.example.com" />
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-phone`}>電話番号 <span className={classes.required}>必須</span></label>
                            <input type="tel" id={`${mode}-phone`} name="phone" value={formData.phone} onChange={handleChange} required placeholder="例：03-1234-5678" />
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-purpose`}>ご相談の目的 <span className={classes.required}>必須</span></label>
                            <div className={classes.selectWrapper}>
                                <select id={`${mode}-purpose`} name="purpose" value={formData.purpose} onChange={handleChange} required>
                                    {purposeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-industry`}>業種 <span className={classes.required}>必須</span></label>
                            <div className={classes.selectWrapper}>
                                <select id={`${mode}-industry`} name="industry" value={formData.industry} onChange={handleChange} required>
                                    {INDUSTRY_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                className={classes.industryPreviewLink}
                                onClick={() => setIsIndustryLpOpen(true)}
                                ref={industryPreviewTriggerRef}
                            >
                                パンフレット
                            </button>
                        </div>

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-phase`}>検討フェーズ <span className={classes.required}>必須</span></label>
                            <div className={classes.selectWrapper}>
                                <select id={`${mode}-phase`} name="projectPhase" value={formData.projectPhase} onChange={handleChange} required>
                                    {PROJECT_PHASE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <fieldset className={classes.optionFieldset}>
                            <legend className={classes.optionLegend}>現在の課題（複数選択可） <span className={classes.optional}>任意</span></legend>
                            <div className={classes.optionGrid}>
                                {hearingOptions.challenges.map((option) => (
                                    <label key={option.value} className={classes.optionCard}>
                                        <input
                                            type="checkbox"
                                            checked={formData.challenges.includes(option.value)}
                                            onChange={(e) => handleMultiSelect('challenges', option.value, e.target.checked)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <fieldset className={classes.optionFieldset}>
                            <legend className={classes.optionLegend}>追加したい機能（複数選択可） <span className={classes.optional}>任意</span></legend>
                            <div className={classes.optionGrid}>
                                {hearingOptions.features.map((option) => (
                                    <label key={option.value} className={classes.optionCard}>
                                        <input
                                            type="checkbox"
                                            checked={formData.desiredFeatures.includes(option.value)}
                                            onChange={(e) => handleMultiSelect('desiredFeatures', option.value, e.target.checked)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        {formData.industry === 'medical' && (
                            <p className={classes.formNotice}>医療・ヘルスケア向けは、予約・問診・受付・会計・勤怠・在庫など現場運用が分かる情報をご記入いただくと提案精度が上がります。</p>
                        )}

                        <div className={classes.formGroup}>
                            <label htmlFor={`${mode}-message`}>自由入力 <span className={classes.optional}>任意</span></label>
                            <textarea
                                id={`${mode}-message`}
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="現状の運用、困っている点、実現したいことなどをご記入ください。"
                                rows="5"
                            />
                        </div>

                        <div className={classes.formGroupCheckbox}>
                            <label>
                                <input type="checkbox" name="agreed" checked={formData.agreed} onChange={handleChange} required />
                                <span><Link to="/privacy" target="_blank">個人情報の取扱い</Link>に同意する</span>
                            </label>
                        </div>

                        <div className={classes.submitAction}>
                            <button type="submit" className={classes.submitBtn} disabled={!formData.agreed || isSubmitting}>{config.submitLabel}</button>
                            {submitError && <p className={classes.submitError}>{submitError}</p>}
                        </div>
                    </form>
                )}
            </div>
        </>
    );
};

export default Home12LeadForm;
